"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExtrasStep = void 0;
const Step_1 = require("../types/Step");
const Utils_1 = require("../Utils");
const PlanStep_1 = require("./PlanStep");
const DetailsStep_1 = require("./DetailsStep");
/**
 * Extras screen (source design §4.3) — the second half of the old combined
 * ServiceOptionsStep. Owns the Total-Connect trio, add-on rows, promo bars and
 * the #service-options-submit bar for the plan picked on the Plan screen.
 * PlanStep drives (re)builds via buildForPlan() on every plan selection; a plan
 * change after the user advanced simply rebuilds silently (selections reset,
 * matching the combined step's behavior).
 */
class ExtrasStep extends Step_1.Step {
    constructor(state) {
        super();
        /* state */
        this.autofillDone = false;
        this.debounce = (func, timeout = 250) => {
            let timer;
            return (...args) => {
                clearTimeout(timer);
                timer = window.setTimeout(() => { func.apply(this, args); }, timeout);
            };
        };
        this.FORM_STATE = state;
        this.selectedAddons = [];
        this.serviceOptionsForm = document.getElementById('service-options-form');
        this.submit = document.getElementById('service-options-submit');
        this.tcContainer = document.getElementById('tc-container');
        this.tcSliderContainer = document.getElementById('tc-slider');
        this.addonsContainer = document.getElementById('addons-container').children[1];
        this.promosContainer = document.getElementById('promos-container').children[1];
        this.addonTemplate = document.getElementById('template-addon');
        this.promoTemplate = document.getElementById('template-promo');
        this.init();
    }
    init() {
        this.serviceOptionsForm.addEventListener('submit', (e) => {
            e.preventDefault();
            if (!this.validate()) {
                return;
            }
            // valid selection, store state
            this.storeState();
            // update summary + goto next step (sibling lookup — never by index).
            // DetailsStep = the merged review+payment screen; setSummarySelection keeps
            // the old ConfirmOfferStep signature/semantics (shared contract).
            const detailsStep = this.FORM_STATE.steps.find(s => s instanceof DetailsStep_1.DetailsStep);
            if (!detailsStep) {
                return;
            }
            detailsStep.setSummarySelection().then(() => {
                detailsStep.unlock();
                this.FORM_STATE.frame.stepper.next();
                (0, Utils_1.sendScrollAdjustment)();
            });
        });
        // debounce resize events for TC rows height calculation
        window.addEventListener('resize', this.debounce(() => {
            this.resizeTCRows();
        }));
        if (this.FORM_STATE.config.devMode) {
            console.info(`[OF] ExtrasStep loaded.`);
        }
        // sp-summary bar: the whole navy bar reads as the CTA (source idiom) — clicks
        // anywhere on it forward to the submit button
        const summaryBar = this.submit.parentElement;
        summaryBar?.addEventListener('click', (e) => {
            if (this.submit.disabled) {
                return;
            }
            if (e.target instanceof Node && this.submit.contains(e.target)) {
                return;
            }
            this.submit.click();
        });
    }
    onShow() {
        if (this.isFirstShow()) {
            // first show event — new name for the split-out screen (the combined
            // ServiceOptionsStep only had firstShowServiceOptions, kept on PlanStep)
            (0, Utils_1.sendEvent)('firstShowServiceExtras', this.FORM_STATE);
        }
        // safety net (debug unlock-all, direct navigation): build for the currently
        // selected plan if PlanStep never pushed a build to us
        if (!this.currentPlan) {
            const planStep = this.FORM_STATE.steps.find(s => s instanceof PlanStep_1.PlanStep);
            const plan = planStep?.getSelectedPlan();
            if (plan) {
                this.buildForPlan(plan);
            }
        }
        // autofill mode (?autofill&addons=..): pick the requested addons, then submit
        if (this.FORM_STATE.config.autofill && !this.autofillDone && this.currentPlan) {
            this.autofillDone = true;
            this.autofillSelection();
            this.submit.click();
        }
    }
    validate() {
        return !!this.currentPlan;
    }
    /**
     * (Re)build every extras section for the given plan. Called by PlanStep on each
     * plan selection — rebuilding resets any prior selections (current behavior).
     */
    buildForPlan(plan) {
        // reset addons + promos containers
        this.resetAddonsAndPromos();
        this.currentPlan = plan;
        // optional page-level flavor: UGPS may deliver the extras heading (raw HTML,
        // same contract as offersHeader) — the built-in EJS heading is the fallback
        const extrasHeader = this.FORM_STATE.ugpsData?.flavor?.extrasHeader;
        if (extrasHeader) {
            const head = document.querySelector('#service-extras .extras-head');
            if (head) {
                head.innerHTML = extrasHeader;
            }
        }
        // build the addons + promos sections
        this.buildTC(plan);
        this.buildAddons(plan);
        this.buildPromos(plan);
        // handle price lock + mybundle "addons"
        this.handlePromoAddons(plan);
        // sync the sp-summary bar (plan name + running total)
        this.updateSummaryBar();
        // show and enable the submit button
        this.submit.parentElement.classList.remove('d-none');
        this.submit.disabled = false;
    }
    resetAddonsAndPromos() {
        this.currentPlan = undefined;
        this.selectedAddons = [];
        this.addonsContainer.innerHTML = ``;
        this.promosContainer.innerHTML = ``;
        this.tcContainer.classList.add('d-none');
        this.addonsContainer.parentElement.classList.add('d-none');
        this.promosContainer.parentElement.classList.add('d-none');
        this.submit.parentElement.classList.add('d-none');
        this.submit.disabled = true;
        this.updateSummaryBar();
    }
    storeState() {
        // store selected options in FORM_STATE
        this.FORM_STATE.userInput.selection = {
            plan: this.currentPlan,
            addons: this.selectedAddons ?? []
        };
        // change submit text to "Update Your Order" (first child stays the text span)
        this.submit.children[0].innerText = 'Update Your Order';
        // NOTE: the old background payment warm-up (PaymentStep.startPaymentComponents)
        // is gone — payment now lives INSIDE DetailsStep, which owns starting its own
        // components; nothing outside DetailsStep may reference startPaymentComponents.
    }
    /**
     * Keep the sp-summary submit bar (plan name + running monthly total) in sync with
     * the current selection. Called on every plan/TC/addon/promo selection change.
     * @private
     */
    updateSummaryBar() {
        const nameEl = document.getElementById('sp-sum-plan');
        const totalEl = document.getElementById('sp-sum-total');
        if (!nameEl || !totalEl) {
            return;
        }
        if (!this.currentPlan) {
            nameEl.textContent = '';
            totalEl.textContent = '';
            return;
        }
        nameEl.textContent = this.currentPlan.flavor?.title ?? this.currentPlan.title;
        const addonsMonthly = (this.selectedAddons ?? [])
            .filter(a => a.frequency !== 'ONE_TIME')
            .reduce((sum, a) => sum + (a.price || 0), 0);
        totalEl.textContent = `Total $${Math.round((this.currentPlan.monthlyPrice || 0) + addonsMonthly)}/mo`;
    }
    /**
     * Build an addon's price label the same way plan cards build theirs: prefer the
     * UGPS flavor action (`button`), and fall back to the numeric `price` when it's absent.
     * @private
     */
    addonActionText(addon) {
        let action = addon.flavor.button;
        if (action === null || action === undefined || action === '') {
            action = addon.price === 0 ? 'Free' : `$${addon.price} / Month`;
        }
        return action === 'Free' ? 'Free' : `+ ${action}`;
    }
    buildTC(plan) {
        let tcAddons = plan.addons?.filter(a => a.title.toUpperCase().includes('TOTAL-CONNECT')) || [];
        let mandatoryTC = tcAddons.find(a => a.type === 'MANDATORY');
        // hide the tc wrapper, and all tc slides
        this.tcContainer.classList.add('d-none');
        this.tcSliderContainer.parentElement.setAttribute('data-slides', String(tcAddons.length + 1));
        // reset the tc grid + remove existing listeners (clone-swap); every slide starts
        // hidden — the availability logic below reveals the applicable tiers
        let slides = Array.from(this.tcSliderContainer.children);
        slides.forEach(el => {
            let newEl = el.cloneNode(true);
            newEl.classList.add('d-none');
            el.parentNode?.replaceChild(newEl, el);
        });
        // re-refernce the slides
        slides = Array.from(this.tcSliderContainer.children);
        if (tcAddons.length > 0) {
            if (mandatoryTC) {
                // show mandatory tc slide only
                let slide;
                if (mandatoryTC.title?.toUpperCase() === 'TOTAL-CONNECT-MAX') {
                    slide = slides[2];
                }
                else if (mandatoryTC.title?.toUpperCase() === 'TOTAL-CONNECT-REGULAR') {
                    slide = slides[1];
                }
                else {
                    slide = slides[0];
                }
                slide.classList.remove('d-none');
                slide.firstElementChild.click();
                this.selectedAddons.push(mandatoryTC);
            }
            else {
                // show applicable tc slides
                slides.forEach((el, index) => {
                    // always show TC none
                    if (index === 0) {
                        el.classList.remove('d-none');
                        return;
                    }
                    // show other slides only if the addon is available for this plan
                    let forAttr = el.firstElementChild.getAttribute('for')?.toUpperCase();
                    let addon = tcAddons.find(a => forAttr === `ADDON-${a.title?.toUpperCase()}`);
                    if (addon) {
                        el.classList.remove('d-none');
                    }
                });
            }
            // show the tc wrapper
            this.tcContainer.classList.remove('d-none');
            // standardize tc slide heights
            this.resizeTCRows();
            slides.forEach((el, index) => {
                let label = el.firstElementChild;
                let forAttr = label.getAttribute('for')?.toUpperCase();
                let addon = tcAddons.find(a => forAttr === `ADDON-${a.title?.toUpperCase()}`);
                // adjust the displayed price from the UGPS addon flavor, the same way plan cards do
                let actionBtn = label.querySelector('.svc-buy button');
                if (addon && actionBtn) {
                    actionBtn.textContent = this.addonActionText(addon);
                }
                // ALL marketing copy on the card flows from UGPS flavor (governance rule:
                // flavor is the source of truth; the EJS copy is only the fallback when a
                // field is absent). Mapping per docs/ORDER2_FLAVOR.md: title → name,
                // bottomText → description, subtitle → value line, topText → feature list
                // (HTML <li>s). Basic Connect (slide 0) has NO catalog addon — it is the
                // absence of Total Connect — so its copy stays static by design.
                if (addon) {
                    const f = addon.flavor ?? {};
                    const nameEl = label.querySelector('.svc-name');
                    if (nameEl && f.title) {
                        nameEl.innerHTML = f.title;
                    }
                    const descEl = label.querySelector('.svc-desc');
                    if (descEl && f.bottomText) {
                        descEl.innerHTML = f.bottomText;
                    }
                    const valueEl = label.querySelector('.svc-value');
                    if (valueEl && f.subtitle) {
                        valueEl.innerHTML = f.subtitle;
                    }
                    const featsEl = label.querySelector('.svc-feats');
                    if (featsEl && f.topText) {
                        featsEl.innerHTML = f.topText;
                    }
                }
                label.addEventListener('click', (e) => {
                    label.children[0].checked = true;
                    // set selected total connect addon
                    if (addon?.type !== 'MANDATORY') {
                        this.selectedAddons = this.selectedAddons?.filter(a => !a.title.toUpperCase().includes('TOTAL-CONNECT')) || [];
                        if (addon && addon.title?.toUpperCase() !== 'TOTAL-CONNECT-NONE')
                            this.selectedAddons.push(addon);
                    }
                    // (the svc-trio is a grid — no carousel slide needed)
                    this.updateSummaryBar();
                    this.revealContinue();
                    if (!e.target || e.target?.tagName !== 'A') {
                        e.preventDefault();
                    }
                });
            });
            // select TC regular if available, then TC none otherwise
            if (!mandatoryTC) {
                if (!slides[1].classList.contains('d-none')) {
                    slides[1].firstElementChild.click();
                }
                else if (!slides[0].classList.contains('d-none')) {
                    slides[0].firstElementChild.click();
                }
            }
        }
    }
    buildAddons(plan) {
        // filter out total connect addons
        let addons = plan.addons
            ?.filter(a => !a.title.toUpperCase().includes('TOTAL-CONNECT'))
            ?.filter(a => a.type !== 'PROMOTION' && a.type !== 'PRICELOCK' && a.type !== 'MYBUNDLE') || [];
        // hide the addons wrappers
        this.addonsContainer.parentElement.classList.add('d-none');
        // build addons html
        for (let [index, addon] of addons.entries()) {
            let isMandatory = addon.type === 'MANDATORY';
            let isPreselected = addon.type === 'PRESELECTED';
            let template = this.addonTemplate.innerHTML;
            template = (0, Utils_1.templateReplace)(template, 'ADDON-ID', addon.title);
            template = (0, Utils_1.templateReplace)(template, 'ADDON-TITLE', addon.flavor.title ?? addon.title);
            template = (0, Utils_1.templateReplace)(template, 'ADDON-IMG', addon.flavor.image ?? '');
            template = (0, Utils_1.templateReplace)(template, 'ADDON-DESCRIPTION', addon.flavor.bottomText ?? '');
            template = (0, Utils_1.templateReplace)(template, 'ADDON-ACTION', this.addonActionText(addon));
            // add it to the DOM
            this.addonsContainer.insertAdjacentHTML('beforeend', template);
            // attach addon selection handler
            const addonCard = document.querySelector(`label[for="addon-${addon.title}"]`);
            const addonCheckbox = document.getElementById(`addon-${addon.title}`);
            // FREE prices read as green small-caps (source .xprice.free)
            const addonPrice = addonCard.querySelector('.xprice');
            if (addonPrice && this.addonActionText(addon) === 'Free') {
                addonPrice.textContent = 'FREE';
                addonPrice.classList.add('free');
            }
            if (isMandatory || isPreselected) {
                this.selectedAddons.push(addon);
                addonCheckbox.checked = true;
            }
            addonCard.addEventListener('click', (e) => {
                let addon = addons.find(a => a.title === addonCard.dataset.addonId);
                if (!addon) {
                    return;
                }
                // we're trying to uncheck an addon
                if (addonCheckbox.checked) {
                    this.selectedAddons.push(addon);
                    // event
                    (0, Utils_1.sendGEvent)('select_item', this.FORM_STATE, {
                        item_id: addon.title,
                        item_name: addon?.flavor?.title ?? addon.title,
                        price: addon.price,
                        index: index,
                        item_category: 'Addon',
                        type: addon.type
                    });
                }
                else {
                    // prevent unchecking mandatory addons
                    if (addon.type === 'MANDATORY') {
                        return;
                    }
                    this.selectedAddons = this.selectedAddons?.filter(a => a.title !== addon.title) || [];
                }
                this.updateSummaryBar();
                this.revealContinue();
            });
        }
        // show the addons wrapper if applicable
        if (addons.length > 0) {
            this.addonsContainer.parentElement.classList.remove('d-none');
        }
    }
    buildPromos(plan) {
        // filter out promo addons
        let promos = plan.addons?.filter(a => a.type === 'PROMOTION') || [];
        // hide the promos wrapper + clear out modals
        this.promosContainer.parentElement.classList.add('d-none');
        document.querySelectorAll('div.modal').forEach(m => m.remove());
        // build promos html
        for (let [index, promo] of promos.entries()) {
            let template = this.promoTemplate.innerHTML;
            template = (0, Utils_1.templateReplace)(template, 'PROMO-ID', promo.title);
            template = (0, Utils_1.templateReplace)(template, 'PROMO-TITLE', promo.flavor.title ?? promo.title);
            template = (0, Utils_1.templateReplace)(template, 'PROMO-IMG', promo.flavor.image ?? '');
            template = (0, Utils_1.templateReplace)(template, 'PROMO-TOPTEXT', promo.flavor.topText ?? '');
            // there's a learn more modal (keep the data-bs-* modal trigger contract untouched)
            if (promo.flavor.learnMore) {
                template = (0, Utils_1.templateReplace)(template, 'PROMO-LEARNMORE', `<button type="button" class="svc-learnmore" data-bs-toggle="modal" data-bs-target="#${promo.title}-modal">Promotion Eligibility</button>`);
                document.body.insertAdjacentHTML('beforeend', promo.flavor.learnMore);
            }
            else {
                template = (0, Utils_1.templateReplace)(template, 'PROMO-LEARNMORE', '');
            }
            // add it to the DOM
            this.promosContainer.insertAdjacentHTML('beforeend', template);
            // attach promo selection handler
            const promoCard = document.querySelector(`label[for="addon-${promo.title}"]`);
            const promoCheckbox = document.getElementById(`addon-${promo.title}`);
            promoCard.addEventListener('click', (e) => {
                // clicks on the eligibility-modal trigger shouldn't toggle the promo
                if (e.target?.closest?.('.svc-learnmore')) {
                    return;
                }
                let promo = promos.find(a => a.title === promoCard.dataset.addonId);
                if (!promo) {
                    return;
                }
                // we're trying to uncheck an addon
                if (promoCheckbox.checked) {
                    this.selectedAddons.push(promo);
                    // event
                    (0, Utils_1.sendGEvent)('select_promotion', this.FORM_STATE, {
                        item_id: promo.title,
                        item_name: promo?.flavor?.title ?? promo.title,
                        index: index,
                        item_category: 'Promotion',
                        item_brand: 'Wire 3',
                        location_id: this.FORM_STATE.userInput.profile?.address
                    });
                }
                else {
                    this.selectedAddons = this.selectedAddons?.filter(a => a.title !== promo.title) || [];
                }
                this.updateSummaryBar();
                this.revealContinue();
            });
        }
        // show the promos wrapper if applicable
        if (promos.length > 0) {
            this.promosContainer.parentElement.classList.remove('d-none');
        }
    }
    handlePromoAddons(plan) {
        let priceLockAddon = plan.addons?.find(a => a.type === 'PRICELOCK');
        if (priceLockAddon) {
            this.selectedAddons.push(priceLockAddon);
        }
        let myBundleAddon = plan.addons?.find(a => a.type === 'MYBUNDLE');
        if (myBundleAddon) {
            this.selectedAddons.push(myBundleAddon);
        }
        this.renderMyBundleLine(myBundleAddon);
    }
    /**
     * Informational free-streaming (MyBundle) line — not selectable, but it carries the
     * selected-card idiom (green ✓ "Included" chip + FREE as its rate) so it clearly
     * reads as part of the order (source mobile-audit port). Flavor is the transitional
     * override ('Free streaming' + months line).
     */
    renderMyBundleLine(myBundleAddon) {
        const line = document.getElementById('mybundle-line');
        if (!line) {
            return;
        }
        if (!myBundleAddon) {
            line.classList.add('d-none');
            line.innerHTML = '';
            return;
        }
        const f = myBundleAddon.flavor ?? {};
        // the rate chip derives from the STRUCTURED price — FREE only when it costs $0
        const rate = (myBundleAddon.price ?? 0) === 0
            ? '<span class="svc-rate-free">FREE</span>'
            : `<span class="svc-rate-free">+$${Math.round(myBundleAddon.price)}/mo</span>`;
        const tv = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="13" rx="2"/><path d="m17 2-5 5-5-5"/></svg>';
        line.innerHTML = `
			<div class="svc-mybundle selected">
				<span class="svc-mybundle-ic">${tv}</span>
				<div class="svc-mybundle-main">
					<div class="svc-name">${f.title || 'Free streaming'} ${rate}</div>
					<div class="svc-desc">${f.bottomText || ''}</div>
				</div>
				<span class="svc-pick"><span class="svc-pick-on">&#10003; Included</span></span>
			</div>`;
        line.classList.remove('d-none');
    }
    /**
     * A pick must never leave the CTA out of sight — on mobile the extras summary bar
     * can sit a full card below the tapped tier (source revealExtrasContinue port).
     */
    revealContinue() {
        const bar = document.getElementById('service-options-submit')?.closest('.sp-summary')
            ?? document.getElementById('service-options-submit');
        if (!bar) {
            return;
        }
        const r = bar.getBoundingClientRect();
        if (r.bottom > window.innerHeight || r.top < 0) {
            bar.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }
    /**
     * Legacy Total-Connect row-height equalizer — intentionally a NO-OP on order2.
     * The .svc-trio grid stretches all three svc-cards to equal height and .svc-buy /
     * .svc-pick pin to the card bottom, so no inline min-heights are needed. Kept because
     * the debounced window-resize handler still calls it.
     * @private
     */
    resizeTCRows() { }
    /**
     * Autofill mode (?autofill&addons=..): click the requested addon rows; default to
     * TC-None unless a Total-Connect tier was explicitly requested (old-step semantics).
     * @private
     */
    autofillSelection() {
        let addons = (this.FORM_STATE.config.urlParams.get('addons') || '').split(',');
        if (addons && addons.length > 0) {
            for (let addonTitle of addons) {
                let addonCard = this.addonsContainer.querySelector(`label[for="addon-${addonTitle}"]`);
                if (addonCard) {
                    addonCard.click();
                }
            }
        }
        // should we check TC-None?
        if (!addons || !addons.some(a => a.toUpperCase().startsWith('TOTAL-CONNECT'))) {
            let tcNone = this.tcContainer.querySelector(`label[for="addon-Total-Connect-None"]`);
            if (tcNone) {
                tcNone.click();
            }
        }
    }
}
exports.ExtrasStep = ExtrasStep;
